import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { API_ENDPOINTS, CATEGORIES } from '../config/api';
import { Star, Sparkles, Trash2, Loader2, Search, X, AlertTriangle, Mail, Download, CheckSquare, Square } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';

const AdminPage = () => {
  const { user, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('blogs');
  
  // Blog-related state
  const [blogPage, setBlogPage] = useState(1);
  const [blogSearchQuery, setBlogSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [minViews, setMinViews] = useState('');
  const [maxViews, setMaxViews] = useState('');
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, blog: null });
  
  // Email subscription-related state
  const [emailPage, setEmailPage] = useState(1);
  const [emailSearchQuery, setEmailSearchQuery] = useState('');
  const [emailSourceFilter, setEmailSourceFilter] = useState('');
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [deleteEmailModal, setDeleteEmailModal] = useState({ isOpen: false, email: null });
  
  const queryClient = useQueryClient();

  // Blog queries
  const { data: blogData, isLoading: blogLoading } = useQuery({
    queryKey: ['blogs', 'admin', blogPage, blogSearchQuery, selectedCategory, minViews, maxViews],
    queryFn: async () => {
      const params = {
        page: blogPage,
        limit: 20,
      };
      if (blogSearchQuery) params.search = blogSearchQuery;
      if (selectedCategory) params.category = selectedCategory;
      if (minViews) params.minViews = minViews;
      if (maxViews) params.maxViews = maxViews;
      
      const response = await api.get(API_ENDPOINTS.BLOGS.ADMIN_ALL, { params });
      return response.data;
    },
    enabled: activeTab === 'blogs',
  });

  // Email subscription queries
  const { data: emailData, isLoading: emailLoading } = useQuery({
    queryKey: ['email-subscriptions', emailPage, emailSearchQuery, emailSourceFilter],
    queryFn: async () => {
      const params = {
        page: emailPage,
        limit: 20,
      };
      if (emailSearchQuery) params.search = emailSearchQuery;
      if (emailSourceFilter) params.source = emailSourceFilter;
      
      const response = await api.get(API_ENDPOINTS.EMAIL_SUBSCRIPTIONS.LIST, { params });
      return response.data;
    },
    enabled: activeTab === 'emails',
  });
  
  const clearBlogFilters = () => {
    setSelectedCategory('');
    setMinViews('');
    setMaxViews('');
    setBlogSearchQuery('');
    setBlogPage(1);
  };
  
  const clearEmailFilters = () => {
    setEmailSourceFilter('');
    setEmailSearchQuery('');
    setEmailPage(1);
    setSelectedEmails([]);
  };
  
  const hasActiveBlogFilters = selectedCategory || minViews || maxViews || blogSearchQuery;
  const hasActiveEmailFilters = emailSourceFilter || emailSearchQuery;

  // Blog mutations
  const featureMutation = useMutation({
    mutationFn: async (blogId) => {
      const response = await api.put(API_ENDPOINTS.BLOGS.ADMIN_FEATURE(blogId));
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['blogs', 'admin']);
      toast.success('Blog featured status updated');
    },
    onError: () => {
      toast.error('Failed to update featured status');
    },
  });

  const sponsorMutation = useMutation({
    mutationFn: async (blogId) => {
      const response = await api.put(API_ENDPOINTS.BLOGS.ADMIN_SPONSOR(blogId));
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['blogs', 'admin']);
      toast.success('Blog sponsored status updated');
    },
    onError: () => {
      toast.error('Failed to update sponsored status');
    },
  });

  const deleteBlogMutation = useMutation({
    mutationFn: async (blogId) => {
      await api.delete(API_ENDPOINTS.BLOGS.ADMIN_DELETE(blogId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['blogs', 'admin']);
      toast.success('Blog deleted');
    },
    onError: () => {
      toast.error('Failed to delete blog');
    },
  });

  // Email subscription mutations
  const deleteEmailMutation = useMutation({
    mutationFn: async (emailId) => {
      await api.delete(API_ENDPOINTS.EMAIL_SUBSCRIPTIONS.DELETE(emailId));
    },
    onSuccess: (data, emailId) => {
      queryClient.invalidateQueries(['email-subscriptions']);
      toast.success('Email subscription deleted');
      setSelectedEmails(prev => prev.filter(id => id !== emailId));
    },
    onError: () => {
      toast.error('Failed to delete email subscription');
    },
  });

  const deleteMultipleEmailsMutation = useMutation({
    mutationFn: async (ids) => {
      await api.delete(API_ENDPOINTS.EMAIL_SUBSCRIPTIONS.DELETE_MULTIPLE, { data: { ids } });
    },
    onSuccess: (_, ids) => {
      queryClient.invalidateQueries(['email-subscriptions']);
      toast.success(`${ids.length} email subscription(s) deleted`);
      setSelectedEmails([]);
    },
    onError: () => {
      toast.error('Failed to delete email subscriptions');
    },
  });

  const exportEmailsMutation = useMutation({
    mutationFn: async () => {
      const response = await api.get(API_ENDPOINTS.EMAIL_SUBSCRIPTIONS.EXPORT, {
        responseType: 'blob',
      });
      return response.data;
    },
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `email-subscriptions-${Date.now()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Email subscriptions exported successfully');
    },
    onError: () => {
      toast.error('Failed to export email subscriptions');
    },
  });

  // Email selection handlers
  const handleSelectEmail = (emailId) => {
    setSelectedEmails(prev => 
      prev.includes(emailId) 
        ? prev.filter(id => id !== emailId)
        : [...prev, emailId]
    );
  };

  const handleSelectAllEmails = () => {
    const emails = emailData?.subscriptions || [];
    if (selectedEmails.length === emails.length) {
      setSelectedEmails([]);
    } else {
      setSelectedEmails(emails.map(e => e._id));
    }
  };

  const handleDeleteSelectedEmails = () => {
    if (selectedEmails.length === 0) {
      toast.error('Please select at least one email to delete');
      return;
    }
    if (window.confirm(`Are you sure you want to delete ${selectedEmails.length} email subscription(s)?`)) {
      deleteMultipleEmailsMutation.mutate(selectedEmails);
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-heading dark:text-white mb-4">
            Access Denied
          </h2>
          <p className="text-gray-500 dark:text-gray-500">
            You need admin privileges to access this page.
          </p>
        </div>
      </div>
    );
  }

  const blogs = blogData?.blogs || [];
  const blogPagination = blogData?.pagination || {};
  const emails = emailData?.subscriptions || [];
  const emailPagination = emailData?.pagination || {};

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-heading font-bold text-heading dark:text-white mb-8">
          Admin Panel
        </h1>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('blogs')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'blogs'
                  ? 'border-accent text-accent'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Blogs
            </button>
            <button
              onClick={() => setActiveTab('emails')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'emails'
                  ? 'border-accent text-accent'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Email Subscriptions
            </button>
          </div>
        </div>

        {/* Blogs Tab */}
        {activeTab === 'blogs' && (
          <>
            {/* Blog Filters */}
            <div className="mb-6 space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={blogSearchQuery}
                  onChange={(e) => {
                    setBlogSearchQuery(e.target.value);
                    setBlogPage(1);
                  }}
                  placeholder="Search blogs..."
                  className="w-full px-4 py-2 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent dark:bg-gray-700 dark:text-white"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Category
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => {
                      setSelectedCategory(e.target.value);
                      setBlogPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">All Categories</option>
                    {CATEGORIES.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Min Views
                  </label>
                  <input
                    type="number"
                    value={minViews}
                    onChange={(e) => {
                      setMinViews(e.target.value);
                      setBlogPage(1);
                    }}
                    placeholder="Min"
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent dark:bg-gray-700 dark:text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Max Views
                  </label>
                  <input
                    type="number"
                    value={maxViews}
                    onChange={(e) => {
                      setMaxViews(e.target.value);
                      setBlogPage(1);
                    }}
                    placeholder="Max"
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent dark:bg-gray-700 dark:text-white"
                  />
                </div>
                
                <div className="flex items-end">
                  {hasActiveBlogFilters && (
                    <button
                      onClick={clearBlogFilters}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      <X className="w-4 h-4" />
                      <span>Clear Filters</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {blogLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-accent" />
              </div>
            ) : (
              <>
                <div className="card overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                          Title
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                          Author
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                          Category
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                          Views
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {blogs.map((blog) => (
                        <tr key={blog._id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-2">
                              <span className="font-medium text-heading dark:text-white">
                                {blog.title}
                              </span>
                              {blog.featured && (
                                <Star className="w-4 h-4 text-yellow-500 fill-current" />
                              )}
                              {blog.sponsored && (
                                <Sparkles className="w-4 h-4 text-accent" />
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                            {blog.author?.username || 'Unknown'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                            {blog.category}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`px-2 py-1 rounded text-xs font-medium ${
                                blog.status === 'published'
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                  : blog.status === 'draft'
                                  ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              }`}
                            >
                              {blog.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                            {blog.views || 0}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => featureMutation.mutate(blog._id)}
                                className={`p-2 rounded ${
                                  blog.featured
                                    ? 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900 dark:text-yellow-400'
                                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                                title="Toggle Featured"
                              >
                                <Star className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => sponsorMutation.mutate(blog._id)}
                                className={`p-2 rounded ${
                                  blog.sponsored
                                    ? 'bg-accent text-white'
                                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                                title="Toggle Sponsored"
                              >
                                <Sparkles className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setDeleteModal({ isOpen: true, blog })}
                                className="p-2 rounded bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-800"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {blogPagination.totalPages > 1 && (
                  <div className="flex items-center justify-center space-x-2 mt-8">
                    <button
                      onClick={() => setBlogPage((p) => Math.max(1, p - 1))}
                      disabled={blogPage === 1}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Previous
                    </button>
                    <span className="px-4 py-2">
                      Page {blogPage} of {blogPagination.totalPages}
                    </span>
                    <button
                      onClick={() => setBlogPage((p) => Math.min(blogPagination.totalPages, p + 1))}
                      disabled={blogPage === blogPagination.totalPages}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Email Subscriptions Tab */}
        {activeTab === 'emails' && (
          <>
            {/* Email Filters and Actions */}
            <div className="mb-6 space-y-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={emailSearchQuery}
                    onChange={(e) => {
                      setEmailSearchQuery(e.target.value);
                      setEmailPage(1);
                    }}
                    placeholder="Search emails..."
                    className="w-full px-4 py-2 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div className="sm:w-48">
                  <select
                    value={emailSourceFilter}
                    onChange={(e) => {
                      setEmailSourceFilter(e.target.value);
                      setEmailPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">All Sources</option>
                    <option value="platform">Platform</option>
                    <option value="blog">Blog</option>
                  </select>
                </div>
                {hasActiveEmailFilters && (
                  <button
                    onClick={clearEmailFilters}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    <X className="w-4 h-4" />
                    <span>Clear</span>
                  </button>
                )}
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {selectedEmails.length > 0 && (
                    <button
                      onClick={handleDeleteSelectedEmails}
                      disabled={deleteMultipleEmailsMutation.isPending}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Delete Selected ({selectedEmails.length})</span>
                    </button>
                  )}
                </div>
                <button
                  onClick={() => exportEmailsMutation.mutate()}
                  disabled={exportEmailsMutation.isPending}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  <Download className="w-4 h-4" />
                  <span>Export to Excel</span>
                </button>
              </div>
            </div>

            {emailLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-accent" />
              </div>
            ) : (
              <>
                <div className="card overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-4 py-3 text-left">
                          <button
                            onClick={handleSelectAllEmails}
                            className="flex items-center"
                          >
                            {selectedEmails.length === emails.length && emails.length > 0 ? (
                              <CheckSquare className="w-5 h-5 text-accent" />
                            ) : (
                              <Square className="w-5 h-5 text-gray-400" />
                            )}
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                          Email
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                          Source
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                          Subscribed At
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {emails.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                            No email subscriptions found
                          </td>
                        </tr>
                      ) : (
                        emails.map((email) => (
                          <tr key={email._id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                            <td className="px-4 py-3">
                              <button
                                onClick={() => handleSelectEmail(email._id)}
                                className="flex items-center"
                              >
                                {selectedEmails.includes(email._id) ? (
                                  <CheckSquare className="w-5 h-5 text-accent" />
                                ) : (
                                  <Square className="w-5 h-5 text-gray-400" />
                                )}
                              </button>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center space-x-2">
                                <Mail className="w-4 h-4 text-gray-400" />
                                <span className="font-medium text-heading dark:text-white">
                                  {email.email}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`px-2 py-1 rounded text-xs font-medium ${
                                  email.source === 'platform'
                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                    : 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                                }`}
                              >
                                {email.source}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                              {formatDistanceToNow(new Date(email.subscribedAt), { addSuffix: true })}
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`px-2 py-1 rounded text-xs font-medium ${
                                  email.isActive
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                }`}
                              >
                                {email.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <button
                                onClick={() => setDeleteEmailModal({ isOpen: true, email })}
                                className="p-2 rounded bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-800"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {emailPagination.totalPages > 1 && (
                  <div className="flex items-center justify-center space-x-2 mt-8">
                    <button
                      onClick={() => setEmailPage((p) => Math.max(1, p - 1))}
                      disabled={emailPage === 1}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Previous
                    </button>
                    <span className="px-4 py-2">
                      Page {emailPage} of {emailPagination.totalPages}
                    </span>
                    <button
                      onClick={() => setEmailPage((p) => Math.min(emailPagination.totalPages, p + 1))}
                      disabled={emailPage === emailPagination.totalPages}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Delete Blog Confirmation Modal */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-lg font-heading font-bold text-heading dark:text-white">
                  Delete Blog
                </h3>
              </div>
              
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Are you sure you want to delete <strong className="text-heading dark:text-white">"{deleteModal.blog?.title}"</strong>? 
                This action cannot be undone.
              </p>
              
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => setDeleteModal({ isOpen: false, blog: null })}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (deleteModal.blog) {
                      deleteBlogMutation.mutate(deleteModal.blog._id);
                      setDeleteModal({ isOpen: false, blog: null });
                    }
                  }}
                  disabled={deleteBlogMutation.isPending}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors flex items-center space-x-2"
                >
                  {deleteBlogMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      <span>Delete</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Email Confirmation Modal */}
      {deleteEmailModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-lg font-heading font-bold text-heading dark:text-white">
                  Delete Email Subscription
                </h3>
              </div>
              
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Are you sure you want to delete <strong className="text-heading dark:text-white">"{deleteEmailModal.email?.email}"</strong>? 
                This action cannot be undone.
              </p>
              
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => setDeleteEmailModal({ isOpen: false, email: null })}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (deleteEmailModal.email) {
                      deleteEmailMutation.mutate(deleteEmailModal.email._id);
                      setDeleteEmailModal({ isOpen: false, email: null });
                    }
                  }}
                  disabled={deleteEmailMutation.isPending}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors flex items-center space-x-2"
                >
                  {deleteEmailMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      <span>Delete</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
